import { SiteProperties } from '@thg-commerce/enterprise-aurora-edge/horizon-schema'

import { ProductChoiceType } from '../../config'

import { ChoiceIcon } from './transformers/choice'
import {
  ChoiceSelectedVariantMap,
  productOptionsMap as productOptionsMapTransformer,
} from './transformers/productOptionsMap'
import { HorizonOption, Price, Variant, VariantOptions } from './types'

export const UNSELECTED_KEY = 'unselected'

export interface SelectedProductOptions {
  productOptions: VariantOptions | null
  selectedChoices: string[]
}

interface ProductOptionsProps {
  variants: Variant[]
  selectedVariant: Variant
  siteProperties: SiteProperties
  horizonOptions: HorizonOption[]
  productOptionsThemeOrder: number[]
  sortedHorizonOptionKeys: string[]
  showOutOfStockAsDisabled: boolean
  displayNotifyWhenInStock: boolean
  productChoiceTypeMap?: {
    [key: string]: {
      type: ProductChoiceType
      propertyKey: string
      placeholderPropertyKey: string
    }
  }
  optionsOrder?: number[]
  optionKeysToFilter?: string[]
  optionKeysToApplyAttribute?: string[]
  linkedOnOptionKey?: string | null
  icon?: ChoiceIcon | null
  preselectVariant?: boolean
  range?: {
    priceRange?: {
      minimum: Price
      maximum: Price
    }
    rrpRange?: {
      minimum: Price
      maximum: Price
    }
  }
}

export const productOptions = (
  props: ProductOptionsProps,
): SelectedProductOptions => {
  const productOptionsMap = productOptionsMapTransformer({
    variants: props.variants,
    selectedVariant: props.selectedVariant,
    horizonOptions: props.horizonOptions,
    productChoiceTypeMap: props.productChoiceTypeMap,
    siteProperties: props.siteProperties,
    optionsOrder: props.optionsOrder,
    optionKeysToFilter: props.optionKeysToFilter,
    optionKeysToApplyAttribute: props.optionKeysToApplyAttribute,
    showOutOfStockAsDisabled: props.showOutOfStockAsDisabled,
    icon: props.icon,
    sortedHorizonOptionKeys: props.sortedHorizonOptionKeys,
    linkedOnOptionKey: props.linkedOnOptionKey,
    displayNotifyWhenInStock: props.displayNotifyWhenInStock,
    range: props.range,
  })

  const singleOption = props.sortedHorizonOptionKeys.length === 1

  const {
    selectedChoices,
    productOptions,
  } = props.sortedHorizonOptionKeys.reduce<{
    productOptions: VariantOptions | null
    selectedChoices: string[]
    currentMap: VariantOptions | ChoiceSelectedVariantMap | null
  }>(
    (accumulator, sortedOptionKey) => {
      const choice = props.selectedVariant.choices.find(
        (choice) => choice.optionKey === sortedOptionKey,
      )

      if (!choice) {
        return accumulator
      }

      if (!accumulator.currentMap) {
        accumulator.currentMap = productOptionsMap
      }

      const enforceUnselected =
        !props.preselectVariant &&
        accumulator.currentMap?.[UNSELECTED_KEY] &&
        sortedOptionKey !== props.linkedOnOptionKey

      const currentKey =
        enforceUnselected && (singleOption || props.linkedOnOptionKey)
          ? UNSELECTED_KEY
          : choice.key

      const currentMapValue = accumulator.currentMap?.[currentKey]

      accumulator.selectedChoices.push(currentKey)

      if (currentMapValue?.options) {
        accumulator.productOptions = currentMapValue
        return accumulator
      }

      if (currentMapValue) {
        accumulator.currentMap = currentMapValue
      }

      return accumulator
    },
    {
      selectedChoices: [],
      productOptions: null,
      currentMap: null,
    },
  )

  return { selectedChoices, productOptions }
}
